import { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Edit, Trash2 } from 'lucide-react';
import { useModules } from '../hooks/useModules';

export const AdminModulesTab = ({ space = 'main' }) => {
  const {
    modules,
    editingModule,
    moduleForm,
    setModuleForm,
    saveModule,
    deleteModule,
    handleEditModule,
    resetModuleForm,
    fetchModules
  } = useModules(space);

  const noun = space === 'platinum_plus' ? 'Training' : 'Module';

  const moduleFormRef = useRef(null);

  useEffect(() => {
    fetchModules();
  }, []);

  const onEdit = (module) => {
    handleEditModule(module);
    moduleFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="space-y-6">
      <Card ref={moduleFormRef}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{editingModule ? `Edit ${noun}` : `Create New ${noun}`}</span>
            {editingModule && (
              <Button variant="ghost" size="sm" onClick={resetModuleForm}>
                Cancel Edit
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input
              value={moduleForm.title}
              onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
              placeholder={`${noun} Title`}
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={moduleForm.description}
              onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
              placeholder={`${noun} Description`}
              rows={3}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Order</Label>
              <Input
                type="number"
                value={moduleForm.order}
                onChange={(e) => setModuleForm({ ...moduleForm, order: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label>Required Previous {noun} (optional)</Label>
              <select
                value={moduleForm.required_previous_module || ''}
                onChange={(e) => setModuleForm({ ...moduleForm, required_previous_module: e.target.value })}
                className="w-full bg-background border border-input rounded-md h-10 px-3 text-sm"
                data-testid="required-previous-module-select"
              >
                <option value="">— None (first {noun.toLowerCase()} or no prerequisite) —</option>
                {modules
                  .filter(m => m.id !== editingModule?.id) // can't depend on self
                  .sort((a, b) => a.order - b.order)
                  .map(m => (
                    <option key={m.id} value={m.id}>
                      {noun} {m.order} — {m.title}
                    </option>
                  ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                This {noun.toLowerCase()} unlocks after the selected one is completed.
              </p>
            </div>
          </div>
          <div>
            <Label>Days Delay (Drip-feed)</Label>
            <Input
              type="number"
              min="0"
              value={moduleForm.days_delay}
              onChange={(e) => setModuleForm({ ...moduleForm, days_delay: parseInt(e.target.value) || 0 })}
              placeholder="Number of days before unlock after previous completion"
            />
            <p className="text-xs text-gray-500 mt-1">Set to 0 for immediate unlock after the previous {noun.toLowerCase()} is completed</p>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              checked={moduleForm.is_locked}
              onCheckedChange={(checked) => setModuleForm({ ...moduleForm, is_locked: checked })}
            />
            <Label>Initially Locked</Label>
          </div>
          <Button onClick={saveModule} className="btn-primary w-full" data-testid="save-module-btn">
            {editingModule ? `Update ${noun}` : `Create ${noun}`}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Existing {noun}s ({modules.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {modules.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No {noun.toLowerCase()}s created yet</p>
          ) : (
            <div className="space-y-3">
              {modules.map((module) => (
                <div key={module.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{module.title}</h3>
                    <p className="text-sm text-gray-600">{module.description}</p>
                    <div className="flex gap-3 mt-2 text-xs text-gray-500">
                      <span>Order: {module.order}</span>
                      <span>{module.is_locked ? '🔒 Locked' : '🔓 Unlocked'}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(module)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {noun}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete "{module.title}" and all its content.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteModule(module.id)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminModulesTab;
